// Grade ↔ graduation-year consistency, mirroring the backend
// milestones.reconcile_graduation_year. A stored graduation_year that's
// inconsistent with the grade (e.g. 2026 for an 11th-grader) makes the student
// look like a graduated senior and dates every senior milestone in the past —
// so the profile builder flags it and offers to fix it.

const GRADE_WORDS = { freshman: 9, sophomore: 10, junior: 11, senior: 12 };

export function gradeNumber(grade) {
    if (grade === null || grade === undefined || typeof grade === 'boolean') return null;
    if (typeof grade === 'number') return grade >= 9 && grade <= 12 ? grade : null;
    const s = String(grade).toLowerCase();
    for (const [word, num] of Object.entries(GRADE_WORDS)) {
        if (s.includes(word)) return num;
    }
    const m = s.match(/\b(9|10|11|12)(?:st|nd|rd|th)?\b/);   // 11, "11th", "grade 11"
    return m ? Number(m[1]) : null;
}

// ADR 0009's 9th/10th-only scope, as a single predicate — NOT just
// `gradeNumber(grade) <= 10`, because `gradeNumber(null) <= 10` is true in
// JS (null coerces to 0). A profile with no grade set yet is unknown, not
// "9th/10th" — this bit a real reachable path in Profile.jsx (unlike
// WelcomePage's grade-pick, whose 4 literal options can never be null).
export function isStoryEligibleGrade(grade) {
    const n = gradeNumber(grade);
    return n !== null && n <= 10;
}

function coerceYear(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

// A current grade-N student: this academic year ends next June (year+1 in the
// fall), and they have (12 - N) academic years left after it.
export function expectedGraduationYear(grade, now = new Date()) {
    const n = gradeNumber(grade);
    if (n === null) return null;
    const academicYearEnd = now.getMonth() + 1 >= 8 ? now.getFullYear() + 1 : now.getFullYear();
    return academicYearEnd + (12 - n);
}

// The grade a stored graduation_year implies right now (9..12), mirroring the
// backend's phase math (_phase_index_for_ym // 3 → grade band; 12 phases =
// Fall/Spring/Summer per grade, #463).
function impliedGradeFromYear(gradYear, now) {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const academicYearEnd = month >= 8 ? year + 1 : year;
    const k = gradYear - academicYearEnd;         // 0 = senior … 3 = freshman
    const gradeBase = (3 - k) * 3;                 // freshman(k=3)=0 … senior(k=0)=9
    const part = month >= 8 ? 0 : (month <= 5 ? 1 : 2);   // fall / spring / summer
    const idx = Math.max(0, Math.min(11, gradeBase + part));
    return 9 + Math.floor(idx / 3);
}

/**
 * Returns null when grade + graduation_year are consistent (or there isn't
 * enough data to compare), else an inconsistency descriptor:
 *   { grade, gradeNum, stored, expected, message }
 *
 * Only the "too advanced" direction is flagged (stored year makes the student
 * look further along than their grade — the case that dates senior milestones
 * in the past). A grade can read a year off around summer, so the safe
 * direction is left alone to avoid false alarms.
 */
export function gradeYearInconsistency(profile, now = new Date()) {
    if (!profile) return null;
    const gradeNum = gradeNumber(profile.grade);
    const stored = coerceYear(profile.graduation_year);
    if (gradeNum === null || stored === null) return null;
    const impliedGrade = impliedGradeFromYear(stored, now);
    if (impliedGrade <= gradeNum) return null;     // consistent, or safe direction
    const expected = expectedGraduationYear(profile.grade, now);
    // Direction-honest copy (founder catch 2026-08-09: a 10th grader with
    // stored 2028 was told "2028 would put you past graduation" — 2028 is
    // EARLIER than their real 2029, i.e. an 11th grader's year, not "past").
    // "Past graduation" is reserved for a stored year that is actually over.
    const gradYearOver = stored < (now.getMonth() + 1 >= 8 ? now.getFullYear() + 1 : now.getFullYear());
    return {
        grade: profile.grade,
        gradeNum,
        stored,
        expected,
        message: `Your grade (Grade ${gradeNum}) and graduation year (${stored}) don't line up — `
            + (gradYearOver
                ? `${stored} is already in the past. `
                : `${stored} is the graduation year of a current Grade ${impliedGrade} student. `)
            + `Based on your grade, you'd graduate ${expected}.`,
    };
}

// "grade 10" for one grade, "grades 9-10" for a span or a list (#5687). One
// definition: the readiness engine's (it may import nothing but ./cdsFactors,
// and its Python twins print the same label, #5688). Not re-exported here:
// gradeYear is in the landing entry chunk, and a re-export pulls the engine in.
